import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface ComboBoxOption {
  value: string | number;
  label: string;
}

interface ComboBoxProps<T extends string | number> {
  options: ComboBoxOption[];
  value: T | null;
  onChange: (value: T | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  allowClear?: boolean;
}

export function ComboBox<T extends string | number>({
  options,
  value,
  onChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search options...",
  emptyMessage = "No option found.",
  isLoading = false,
  triggerClassName,
  contentClassName,
  allowClear = true,
}: ComboBoxProps<T>) {
  const [open, setOpen] = React.useState(false);

  const selectedOption = React.useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  const selectedLabel = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (currentValueLabel: string) => {
    const selectedOpt = options.find((opt) => opt.label.toLowerCase() === currentValueLabel.toLowerCase());
    const selectedValue = selectedOpt ? (selectedOpt.value as T) : null;

    onChange(selectedValue);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setOpen(false);
  };

  if (isLoading) {
    return (
      <Button variant="outline" disabled className={cn("justify-between", triggerClassName)}>
        {placeholder}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between", triggerClassName)}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0", contentClassName)}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {allowClear && value !== null && (
                <CommandItem
                  key="clear-selection"
                  value="__clear__"
                  onSelect={handleClear}
                  className="text-muted-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear selection
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem key={option.value} value={option.label} onSelect={handleSelect}>
                  <Check className={cn("mr-2 h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")} />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
